import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { StatusDot } from './StatusDot'

/**
 * LiveBadge — a small pill that combines a pulsing StatusDot with a label
 * to signal "this data is live / always-fresh" alongside cached data.
 *
 * Use case: distinguish dashboard widgets backed by live SQL queries from
 * Phase-1 Report Builder reports backed by Materialized Views (which need
 * an explicit refresh). Dashboard widgets get `<LiveBadge />`; reports get
 * a "Updated N min ago" footer.
 *
 * Composes existing primitives (StatusDot atom + Tailwind tokens). No new
 * styling primitives — just a labeled, pulsing dot in pill form.
 */
export const liveBadgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-pill font-medium text-caption leading-none transition-colors',
  {
    variants: {
      tone: {
        success: 'bg-ia-success-bg text-ia-success',
        warning: 'bg-ia-warning-bg text-ia-warning',
        error: 'bg-ia-error-bg text-ia-error',
        neutral: 'bg-ia-elevated text-ia-text',
      },
      size: {
        sm: 'h-5 px-2',
        md: 'h-6 px-3',
      },
    },
    defaultVariants: { tone: 'success', size: 'sm' },
  },
)

export interface LiveBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof liveBadgeVariants> {
  /** Visible label. Defaults to "Live". */
  label?: string
  /** When true, the dot animates a slow pulse to draw the eye. Defaults to true. */
  pulsing?: boolean
}

const TONE_TO_DOT_STATUS = {
  success: 'pending', // pending == animate-pulse, the visual we want for "alive"
  warning: 'pending',
  error: 'error',
  neutral: 'inactive',
} as const

export const LiveBadge = React.forwardRef<HTMLSpanElement, LiveBadgeProps>(
  ({ className, tone = 'success', size, label = 'Live', pulsing = true, ...props }, ref) => {
    const dotStatus = pulsing ? TONE_TO_DOT_STATUS[tone ?? 'success'] : 'active'
    return (
      <span
        ref={ref}
        role="status"
        aria-label={`${label} — live data`}
        className={cn(liveBadgeVariants({ tone, size }), className)}
        {...props}
      >
        <StatusDot size="sm" status={dotStatus} aria-hidden="true" />
        <span>{label}</span>
      </span>
    )
  },
)
LiveBadge.displayName = 'LiveBadge'
